import { Link } from 'react-router-dom'
import type { OnboardingChecklist, OnboardingStep } from '../state/onboarding'
import type { ReportsState } from '../state/reports'
import { Button, buttonClass } from './Button'
import { Dialog } from './Dialog'
import { Icon, type IconName } from './Icon'

export interface OnboardingDialogProps {
  isOpen: boolean
  onClose: () => void
  state: ReportsState
  checklist: OnboardingChecklist
  /** Whether the registry is followed, and how to follow it, for its step. */
  registry?: { following: boolean; follow: () => void }
}

const STEPS: {
  id: OnboardingStep
  icon: IconName
  title: string
  text: string
  /** What ticks it, so nobody has to guess. */
  ticks: string
  to: string
  action: string
}[] = [
  {
    id: 'sample',
    icon: 'chart',
    title: 'Look around with the samples',
    text: 'Real assessments of open research software, bundled with the app, show what the dashboard does.',
    ticks: 'Ticks itself once assessments are loaded.',
    to: '/assessment',
    action: 'Open Assessment',
  },
  {
    id: 'sources',
    icon: 'globe',
    title: 'Follow the EVERSE Assessment Registry',
    text: 'EVERSE publishes assessments of research software there; following it loads new ones by themselves.',
    ticks: 'Ticks itself while a source is followed.',
    to: '/welcome',
    action: 'Open Welcome',
  },
  {
    id: 'settings',
    icon: 'settings',
    title: 'Customise appearance and preferences',
    text: 'Light or dark theme, contrast modes, and accessible chart colour palettes.',
    ticks: 'Ticks itself when you visit Settings.',
    to: '/settings',
    action: 'Open Settings',
  },
]

/** A short getting-started checklist: three steps, each with where to do it. */
export function OnboardingDialog({
  isOpen,
  onClose,
  state,
  checklist,
  registry,
}: OnboardingDialogProps) {
  const loaded = state.reports.length > 0
  const done = STEPS.length - checklist.remaining

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      labelledBy="onboarding-dialog-title"
      className="onboarding-dialog"
    >
      <div className="dialog-inner">
        <div className="dialog-head">
          <div className="onboarding-head">
            <span className="icon-badge" aria-hidden="true">
              <Icon name="listChecks" size={20} />
            </span>
            <div>
              <h2 id="onboarding-dialog-title">Getting started</h2>
              <p className="muted m-0">{STEPS.length} steps to find your way around.</p>
            </div>
          </div>
          <Button tone="quiet" size="small" icon="close" iconOnly label="Close" onClick={onClose} />
        </div>

        <p className="small muted m-0 mb-2">
          The steps tick themselves as you do them, and you can tick or untick them yourself.
        </p>

        <div className="onboarding-progress">
          <span className="small">
            <strong>
              {done} of {STEPS.length}
            </strong>{' '}
            {done === STEPS.length ? 'done. You know your way around.' : 'done'}
          </span>
          <span className="onboarding-progress-track" aria-hidden="true">
            <span style={{ width: `${(done / STEPS.length) * 100}%` }} />
          </span>
        </div>

        <ol className="onboarding-steps">
          {STEPS.map((step) => {
            const isDone = checklist.isDone(step.id)
            return (
              <li key={step.id} className={`onboarding-step${isDone ? ' is-done' : ''}`}>
                <input
                  type="checkbox"
                  checked={isDone}
                  onChange={() => checklist.toggle(step.id)}
                  aria-label={`${step.title}, done`}
                />
                <div className="onboarding-step-text">
                  <strong className="row items-center gap-2">
                    <Icon name={step.icon} size={14} />
                    {step.title}
                  </strong>
                  <p className="small muted m-0">{step.text}</p>
                  <p className="onboarding-ticks m-0">{isDone ? 'Done.' : step.ticks}</p>
                </div>
                {step.id === 'sources' && registry && !registry.following ? (
                  <Button
                    size="small"
                    icon="plus"
                    label="Follow the registry"
                    onClick={registry.follow}
                  />
                ) : step.id === 'sample' && !loaded ? (
                  <Button
                    size="small"
                    icon="download"
                    label="Load samples"
                    disabled={state.busy}
                    onClick={async () => {
                      await state.addSamples()
                      onClose()
                    }}
                  />
                ) : (
                  <Link to={step.to} onClick={onClose} className={buttonClass({ size: 'small' })}>
                    <span>{step.action}</span>
                    <Icon name="chevronRight" size={12} />
                  </Link>
                )}
              </li>
            )
          })}
        </ol>

        <div className="onboarding-foot">
          <Button tone="primary" size="small" label="Done" onClick={onClose} />
        </div>
      </div>
    </Dialog>
  )
}
